import { containsWorkflowVariable } from '../../utils/workflow-variable';
import countries from '../constants/countries';
import { contactFieldsOptions, contactFieldOptionsCache } from '../contact-fields-options';

import type { SelectField, StepConfig, StepFormField, TextField, FieldValue, FieldValues } from '../../types/workflow';

const updateContactField: StepConfig = {
  // Design
  icon: 'updateContactField',
  headerTextColor: '#BE25AB',
  headerColor: '#ECD9EF',
  title: 'Update Contact Field',
  text: 'Update Contact Field',
  category: 'Node',
  desc: 'Modifies contact field values for the contact. Cannot Update the contact identifier.',
  primaryKey: 'update {{field}} to {{value}}',

  // Form
  formFields: [
    {
      key: 'field',
      label: 'Field',
      type: 'select',
      searchKey: 'filter[q]',
      defaultValue: '',
      options: contactFieldsOptions,
      onChange: (value: FieldValue, allValues: FieldValues, allFields: Record<string, StepFormField>) => {
        // Check if country_name is selected
        if (value === 'country_name') {
          (allFields.value as SelectField).type = 'select';
          (allFields.value as SelectField).options = countries;
          // reset value to empty string
          allValues.value = '';
          // Clear any lingering validation flags from previous field selection
          (allFields.value as TextField).validation!.email = false;
          (allFields.value as TextField).validation!.phone = false;
          delete (allFields.value as TextField).validation!.textNoSpecialChars;
          delete (allFields.value as TextField).validation!.maxLength;
          return;
        }

        // Adapt value field based on selected contact field type using cached options
        const selectedOption = contactFieldOptionsCache?.[value as string];
        if (selectedOption) {
          if (selectedOption.type === 'list') {
            (allFields.value as SelectField).type = 'select';
            // reset value to empty string
            allValues.value = '';
            // Provide static options array; FormField will map label/value to items
            (allFields.value as SelectField).options = (selectedOption.list_values || []).map((val) => ({
              label: val,
              value: val,
            }));
          } else if (selectedOption.type === 'text' || !selectedOption.type) {
            (allFields.value as TextField).type = 'text';
            // reset value to empty string
            allValues.value = '';
          } else if (selectedOption.type === 'number') {
            (allFields.value as TextField).type = 'number';
            // reset value to empty string
            allValues.value = '';
          }
        }
        if (selectedOption && selectedOption.value === 'email') {
          // Disable validation if variable is used
          const valueField = (allValues as Record<string, FieldValue>).value;
          (allFields.value as TextField).validation!.email = !containsWorkflowVariable(valueField);
          (allFields.value as TextField).validation!.phone = false;
          delete (allFields.value as TextField).validation!.textNoSpecialChars;
          delete (allFields.value as TextField).validation!.maxLength;
        } else if (selectedOption && selectedOption.value === 'phone_number') {
          const valueField = (allValues as Record<string, FieldValue>).value;
          (allFields.value as TextField).validation!.phone = !containsWorkflowVariable(valueField);
          (allFields.value as TextField).validation!.email = false;
          delete (allFields.value as TextField).validation!.textNoSpecialChars;
          delete (allFields.value as TextField).validation!.maxLength;
        } else if (selectedOption && (selectedOption.type === 'text' || !selectedOption.type)) {
          (allFields.value as TextField).validation!.email = false;
          (allFields.value as TextField).validation!.phone = false;
          // Disable textNoSpecialChars validation if variable is used
          const valueField = (allValues as Record<string, FieldValue>).value;
          (allFields.value as TextField).validation!.textNoSpecialChars = !containsWorkflowVariable(valueField);
          (allFields.value as TextField).validation!.maxLength = 255;
        } else {
          (allFields.value as TextField).validation!.email = false;
          (allFields.value as TextField).validation!.phone = false;
          delete (allFields.value as TextField).validation!.textNoSpecialChars;
          delete (allFields.value as TextField).validation!.maxLength;
        }
      },
    },
    {
      key: 'value',
      label: 'Value',
      type: (parentValues: FieldValues) => {
        const field = parentValues.field as string;
        if (field === 'country_name') {
          return 'select';
        }
        // Check if selected field is a custom attribute with type 'list'
        const selectedOption = contactFieldOptionsCache?.[field];
        if (selectedOption && selectedOption.type === 'list') {
          return 'select';
        }
        return 'text';
      },
      useVariables: true,
      defaultValue: '',
      placeholder: 'Enter Text',
      dependsOn: ['field'],
      options: (parentValues: FieldValues) => {
        const field = parentValues.field as string;
        if (field === 'country_name') {
          return countries;
        }
        // Return list values when field is a custom attribute with type 'list'
        const selectedOption = contactFieldOptionsCache?.[field];
        if (selectedOption && selectedOption.type === 'list') {
          return (selectedOption.list_values || []).map((val) => ({
            label: val,
            value: val,
          }));
        }
        return [];
      },
      validation: {
        required: true,
        email: false,
        phone: false,
      },
    },
  ],
};

export default updateContactField;
